import { use_slice_ui } from '@/hooks/store/use_slices';
import { service_open_pose } from '@/services/open_pose/service_open_pose';
import { assert_type_enum, assert_type_enum_return, not_implemented_type } from '@shared/asserts/utils_asserts';
import { useEffect as use_effect, useRef } from 'react';
import * as THREE from 'three';
import { Bone, MeshBasicMaterial, Object3D } from 'three';
import { ConstantsOpenPose } from '../constants_open_pose';
import { EOpenPoseBodyBone } from '../enums/e_open_pose_body_bone';
import { EOpenPoseBodyGroup } from '../enums/e_open_pose_body_group';
import { EOpenPoseBodyNode } from '../enums/e_open_pose_body_node';
import { EOpenPoseCursorAxis } from '../enums/e_open_pose_cursor_axis';
import { EOpenPoseCursorPlane } from '../enums/e_open_pose_cursor_plane';
import { EOpenPoseInteractionMode as EOPIM } from '../enums/e_open_pose_interaction_mode';
import { EOpenPoseTransformControlsMode } from '../enums/e_open_pose_transform_controls_mode';
import { EOpenPoseTransformControlsSpace } from '../enums/e_open_pose_transform_controls_space';
import { EThreejsStub } from '../enums/e_threejs_stub';
import { OpenPoseCursorAxis } from '../open_pose_3d_cursors/open_pose_axis_cursor';
import { OpenPoseCursorPlane } from '../open_pose_3d_cursors/open_pose_cursor_plane';
import { OpenPoseLimbs } from '../open_pose_types/open_pose_limbs';
import { OpenPoseSelectedNodeInfo } from '../open_pose_types/open_pose_selected_node_info';
import { OpenPoseBodyController } from './open_pose_body_controller';
import { OpenPoseBodyEventManager } from './open_pose_body_event_manager';
import { OpenPoseBodyNode } from './open_pose_body_node';
import { open_pose_body_create_template } from './utils/open_pose_body_create_template';
import {
  apply_position_constraints,
  apply_position_constraints_to_vector,
  get_connected_bone_names_for_node,
  get_connected_node_for_hand_foot,
  get_movement_bounds,
  get_parent_nodes,
  set_color,
  set_full_opacity_for_body_links_and_joints,
} from './open_pose_body_utils';
import {
  find_ancestor_pickable,
  find_ancestor_pickable_hand_or_foot,
  get_child_bones,
  get_child_nodes,
  get_node_sphere_mesh,
  is_bone,
  is_hand_or_foot,
  is_pickable,
} from './utils/open_pose_body_parts';

const {
  CURSOR_PART_PLANE,
  DEFAULT_BODY_SIZE,
  INTERSECT_TARGET_POINT_NAME,
  BOUNDING_BOX_COLOR,
  CURSOR_PART_CONE,
  BOUNDING_BOX_NAME,
  COLOR_SELECTED_FOR_BONE,
  JOINT_SPHERE_NAME,
  LINK_NAME,
  BODY_SIZE_DEFAULT_VALUE,
  SELECTED_NODE_SCALE_DIVISOR,
  BONE_PARENT_MAP,
  CURSOR_PART_CYLINDER,
  CURSOR_PLANE_DEFAULT_OPACITY,
  CURSOR_PLANE_UNHOVER_OPACITY,
  CURSOR_PLANE_HOVER_OPACITY,
} = ConstantsOpenPose;

export function use_open_pose_body() {
  /*
   * FIELDS
   */
  const { is_service_open_pose_init } = use_slice_ui(['is_service_open_pose_init']);

  const ref_custom_cursor_plane = useRef<OpenPoseCursorPlane>(new OpenPoseCursorPlane());
  const ref_custom_cursor_axis = useRef<OpenPoseCursorAxis>(new OpenPoseCursorAxis());

  const ref_bounds_helper = useRef<THREE.LineSegments>(EThreejsStub.LINE_SEGMENTS);
  const ref_camera_bounds_helper = useRef<THREE.LineSegments>(EThreejsStub.LINE_SEGMENTS);
  const ref_body_bounds_helper = useRef<THREE.LineSegments>(EThreejsStub.LINE_SEGMENTS);

  const ref_interaction_mode = useRef<EOPIM>(EOPIM.DRAG_NODE_FIXED_LENGTH);

  const ref_node_selected_info = useRef<OpenPoseSelectedNodeInfo>(EThreejsStub.SELECTED_NODE_INFO);
  const ref_node_selected = useRef<Object3D>(EThreejsStub.OBJECT);

  const ref_previous_body_size = useRef(DEFAULT_BODY_SIZE);

  const ref_is_mouse_down = useRef(false);

  const ref_select_event_manager = useRef(new OpenPoseBodyEventManager());

  const ref_raycaster = useRef(EThreejsStub.RAYCASTER);

  const ref_axis_active_axis_name = useRef<EOpenPoseCursorAxis>(EOpenPoseCursorAxis.NONE);
  const ref_axis_active_plane_name = useRef<EOpenPoseCursorPlane>(EOpenPoseCursorPlane.NONE);

  const ref_axis_drag_axis_direction_world = useRef<THREE.Vector3>(new THREE.Vector3());
  const ref_axis_drag_plane_world = useRef<THREE.Plane>(new THREE.Plane());

  const ref_axis_drag_start_gizmo_world_position = useRef<THREE.Vector3>(new THREE.Vector3());
  const ref_axis_drag_start_node_world_position = useRef<THREE.Vector3>(new THREE.Vector3());

  const ref_plane_active_plane_name = useRef<EOpenPoseCursorPlane>(EOpenPoseCursorPlane.NONE);
  const ref_plane_drag_plane_world = useRef<THREE.Plane>(new THREE.Plane());
  const ref_plane_drag_start_gizmo_world_position = useRef<THREE.Vector3>(new THREE.Vector3());
  const ref_plane_drag_start_node_world_position = useRef<THREE.Vector3>(new THREE.Vector3());

  /*
   * EFFECTS
   */

  use_effect(() => {
    if (!is_service_open_pose_init) return;

    ref_raycaster.current = new THREE.Raycaster();

    service_open_pose.transform_controls.removeEventListener('change', on_transform_change);
    service_open_pose.transform_controls.removeEventListener('mouseDown', on_transform_mouse_down);
    service_open_pose.transform_controls.removeEventListener('mouseUp', on_transform_mouse_up);

    service_open_pose.transform_controls.addEventListener('change', on_transform_change);
    service_open_pose.transform_controls.addEventListener('mouseDown', on_transform_mouse_down);
    service_open_pose.transform_controls.addEventListener('mouseUp', on_transform_mouse_up);

    // Show bounds helpers immediately when service is initialized
    setTimeout(() => {
      if (service_open_pose.camera_controller && service_open_pose.billboard_controller) {
        show_bounds_helpers();
      }
    }, 100);

    return () => {
      service_open_pose.transform_controls.removeEventListener('change', on_transform_change);
      service_open_pose.transform_controls.removeEventListener('mouseDown', on_transform_mouse_down);
      service_open_pose.transform_controls.removeEventListener('mouseUp', on_transform_mouse_up);
    };
  }, [service_open_pose.transform_controls]);

  use_effect(() => {
    return () => {
      ref_custom_cursor_plane.current.dispose();
      service_open_pose.scene.remove(ref_custom_cursor_plane.current);

      ref_custom_cursor_axis.current.dispose();
      service_open_pose.scene.remove(ref_custom_cursor_axis.current);

      service_open_pose.scene.remove(ref_bounds_helper.current);
      ref_bounds_helper.current.geometry.dispose();

      if (ref_bounds_helper.current.material instanceof THREE.Material) ref_bounds_helper.current.material.dispose();

      // Cleanup camera bounds helper
      service_open_pose.scene.remove(ref_camera_bounds_helper.current);
      ref_camera_bounds_helper.current.geometry.dispose();
      if (ref_camera_bounds_helper.current.material instanceof THREE.Material) ref_camera_bounds_helper.current.material.dispose();

      // Cleanup body bounds helper
      service_open_pose.scene.remove(ref_body_bounds_helper.current);
      ref_body_bounds_helper.current.geometry.dispose();
      if (ref_body_bounds_helper.current.material instanceof THREE.Material) ref_body_bounds_helper.current.material.dispose();
    };
  }, []);

  /*
   * EVENTS
   */

  function on_mouse_down(event: MouseEvent, canvas: HTMLCanvasElement) {
    const is_left_button = event.button === 0;
    if (!is_left_button) return;

    const camera_current = service_open_pose.camera_controller.get_camera_current();
    const raycaster = ref_raycaster.current;

    const canvas_rect = canvas.getBoundingClientRect();

    const mouse_x = event.clientX - canvas_rect.left;
    const mouse_y = event.clientY - canvas_rect.top;
    const mouse_position_ndc = new THREE.Vector2((mouse_x / canvas.clientWidth) * 2 - 1, -(mouse_y / canvas.clientHeight) * 2 + 1);

    raycaster.setFromCamera(mouse_position_ndc, camera_current);

    if (ref_interaction_mode.current === EOPIM.DRAG_NODE_FIXED_LENGTH && ref_custom_cursor_plane.current?.visible) {
      if (process_plane_cursor_mouse_down(raycaster)) return;
    } else if (ref_interaction_mode.current === EOPIM.DRAG_NODE && ref_custom_cursor_axis.current?.visible) {
      const intersections = raycaster.intersectObject(ref_custom_cursor_axis.current, true);

      if (intersections.is_empty()) return;

      const intersection = intersections[0];

      const intersected_object = intersection.object;

      if (intersected_object.name.includes(CURSOR_PART_CYLINDER) || intersected_object.name.includes(CURSOR_PART_CONE)) {
        if (process_axis_cursor_cylinder_or_cone_mouse_down(intersected_object)) return;
      } else if (intersected_object.name.includes(CURSOR_PART_PLANE)) {
        if (process_axis_cursor_plane_mouse_down(intersected_object)) return;
      }
    }

    const intersections_body: THREE.Intersection[] = raycaster.intersectObjects([service_open_pose.body_controller.body], true);

    const pick_point = intersections_body.find(object_3d => object_3d.object.name === INTERSECT_TARGET_POINT_NAME);

    let intersected_object: THREE.Object3D = EThreejsStub.OBJECT;

    if (pick_point) {
      intersected_object = pick_point.object;
    } else if (intersections_body.length > 0) {
      intersected_object = intersections_body[0].object;
    }

    if (intersected_object.is_stub()) return;

    if (!intersected_object.name.includes(ConstantsOpenPose.JOINT_SPHERE_NAME)) return;
    const node_name = intersected_object.name.replace(ConstantsOpenPose.JOINT_SPHERE_NAME, '');

    const intersected_name = assert_type_enum_return(node_name, EOpenPoseBodyNode);

    let picked_object_3d: THREE.Object3D = intersected_object;

    if (ref_interaction_mode.current === EOPIM.DRAG_NODE) {
      on_drag_node_float_length_mode(intersected_name, picked_object_3d);
    } else if (ref_interaction_mode.current === EOPIM.DRAG_NODE_FIXED_LENGTH) {
      on_drag_node_fixed_length_mode(intersected_name, picked_object_3d);
    }
  }

  function on_mouse_move(event: MouseEvent, canvas: HTMLCanvasElement) {
    const camera_current = service_open_pose.camera_controller.get_camera_current();
    const raycaster = ref_raycaster.current;
    const canvas_rect = canvas.getBoundingClientRect();

    const mouse_x = event.clientX - canvas_rect.left;
    const mouse_y = event.clientY - canvas_rect.top;
    const mouse_position_ndc = new THREE.Vector2((mouse_x / canvas.clientWidth) * 2 - 1, -(mouse_y / canvas.clientHeight) * 2 + 1);

    if (ref_is_mouse_down.current) {
      if (ref_interaction_mode.current === EOPIM.DRAG_NODE || ref_interaction_mode.current === EOPIM.DRAG_NODE_FIXED_LENGTH)
        process_node_drag(mouse_position_ndc, camera_current);
      return;
    }

    raycaster.setFromCamera(mouse_position_ndc, camera_current);

    let previous_hovered_axis_name: EOpenPoseCursorAxis = EOpenPoseCursorAxis.NONE;
    let previous_hovered_plane_name: EOpenPoseCursorPlane = EOpenPoseCursorPlane.NONE;

    if (ref_custom_cursor_plane.current.visible) previous_hovered_plane_name = process_hover_plane(raycaster, previous_hovered_plane_name);

    if (ref_custom_cursor_axis.current.visible) {
      const updated = process_hover_axis(raycaster, previous_hovered_axis_name, previous_hovered_plane_name);

      previous_hovered_axis_name = updated.previous_hovered_axis_name;
      previous_hovered_plane_name = updated.previous_hovered_plane_name;
    }
  }

  function on_mouse_up(event: MouseEvent, canvas: HTMLCanvasElement) {
    const was_dragging = ref_is_mouse_down.current;
    ref_is_mouse_down.current = false;

    if (was_dragging) return process_drag_node_end();

    const camera_current = service_open_pose.camera_controller.get_camera_current();
    const raycaster = ref_raycaster.current;
    const rect = canvas.getBoundingClientRect();

    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;

    raycaster.setFromCamera(new THREE.Vector2((x / canvas.clientWidth) * 2 - 1, -(y / canvas.clientHeight) * 2 + 1), camera_current);

    const intersections: THREE.Intersection[] = raycaster.intersectObjects([service_open_pose.body_controller.body], true);
    const pick_point = intersections.find(object => object.object.name === INTERSECT_TARGET_POINT_NAME);
    const intersected_object: THREE.Object3D = pick_point ? pick_point.object : intersections.length > 0 ? intersections[0].object : EThreejsStub.OBJECT;

    if (!intersected_object || !intersected_object.name.includes(ConstantsOpenPose.JOINT_SPHERE_NAME)) return;

    const node_name = intersected_object.name.replace(ConstantsOpenPose.JOINT_SPHERE_NAME, '');

    const intersected_name = assert_type_enum_return(node_name, EOpenPoseBodyNode);

    let object_3d: THREE.Object3D = intersected_object;

    if (object_3d.is_stub()) {
      ensure_node_selected();

      const world_position = ref_node_selected.current.get_world_position_zero_offset();

      show_cursor_at_position(world_position);
      enforce_selected_node_visual();
    } else if (ref_interaction_mode.current === EOPIM.ROTATE) {
      on_rotate_mode(intersected_name, object_3d);
    } else if (ref_interaction_mode.current === EOPIM.DRAG_NODE) {
      on_drag_node_float_length_mode(intersected_name, object_3d);
    } else if (ref_interaction_mode.current === EOPIM.DRAG_NODE_FIXED_LENGTH) {
      on_drag_node_fixed_length_mode(intersected_name, object_3d);
    } else {
      not_implemented_type(ref_interaction_mode.current);
    }
  }

  /*
   * METHODS
   */

  function get_bone_name_for_node(node_name: string): string | null {
    // Map node names to their corresponding bone enum keys
    const bone_map: Record<string, string> = {
      // Arms
      [EOpenPoseBodyNode.ELBOW_LEFT]: EOpenPoseBodyBone.UPPER_ARM_LEFT,
      [EOpenPoseBodyNode.ELBOW_RIGHT]: EOpenPoseBodyBone.UPPER_ARM_RIGHT,
      [EOpenPoseBodyNode.WRIST_LEFT]: EOpenPoseBodyBone.FOREARM_LEFT,
      [EOpenPoseBodyNode.WRIST_RIGHT]: EOpenPoseBodyBone.FOREARM_RIGHT,

      // Legs
      [EOpenPoseBodyNode.KNEE_LEFT]: EOpenPoseBodyBone.UPPER_LEG_LEFT,
      [EOpenPoseBodyNode.KNEE_RIGHT]: EOpenPoseBodyBone.UPPER_LEG_RIGHT,
      [EOpenPoseBodyNode.ANKLE_LEFT]: EOpenPoseBodyBone.LOWER_LEG_LEFT,
      [EOpenPoseBodyNode.ANKLE_RIGHT]: EOpenPoseBodyBone.LOWER_LEG_RIGHT,

      // Head
      [EOpenPoseBodyNode.NOSE]: EOpenPoseBodyBone.NECK_TO_NOSE,
      [EOpenPoseBodyNode.EYE_LEFT]: EOpenPoseBodyBone.NOSE_TO_EYE_LEFT,
      [EOpenPoseBodyNode.EYE_RIGHT]: EOpenPoseBodyBone.NOSE_TO_EYE_RIGHT,
      [EOpenPoseBodyNode.EAR_LEFT]: EOpenPoseBodyBone.EYE_TO_EAR_LEFT,
      [EOpenPoseBodyNode.EAR_RIGHT]: EOpenPoseBodyBone.EYE_TO_EAR_RIGHT,

      // Shoulders
      [EOpenPoseBodyNode.SHOULDER_LEFT]: EOpenPoseBodyBone.SHOULDER_LEFT,
      [EOpenPoseBodyNode.SHOULDER_RIGHT]: EOpenPoseBodyBone.SHOULDER_RIGHT,

      // Hips
      [EOpenPoseBodyNode.HIP_LEFT]: EOpenPoseBodyBone.THIGH_LEFT,
      [EOpenPoseBodyNode.HIP_RIGHT]: EOpenPoseBodyBone.THIGH_RIGHT,
    };

    const bone_name = bone_map[node_name];
    console.log(`[GET_BONE_NAME] Node ${node_name} -> Bone ${bone_name}`);

    return bone_name;
  }

  function axis_begin_axis_drag(axis_name: EOpenPoseCursorAxis, cursor_world_position: THREE.Vector3, node_world_position: THREE.Vector3) {
    ref_axis_active_axis_name.current = axis_name;
    ref_axis_active_plane_name.current = EOpenPoseCursorPlane.NONE;

    const axis_direction_world =
      axis_name === EOpenPoseCursorAxis.X
        ? new THREE.Vector3(1, 0, 0)
        : axis_name === EOpenPoseCursorAxis.Y
        ? new THREE.Vector3(0, 1, 0)
        : new THREE.Vector3(0, 0, 1);

    ref_axis_drag_axis_direction_world.current.copy(axis_direction_world).normalize();

    ref_axis_drag_start_gizmo_world_position.current.copy(cursor_world_position);
    ref_axis_drag_start_node_world_position.current.copy(node_world_position);

    highlight_axis(axis_name);
  }

  function axis_begin_plane_drag(plane_name: EOpenPoseCursorPlane, cursor_world_position: THREE.Vector3, node_world_position: THREE.Vector3) {
    ref_axis_active_plane_name.current = plane_name;
    ref_axis_active_axis_name.current = EOpenPoseCursorAxis.NONE;

    const plane_normal_world = get_plane_normal_from_name(plane_name);

    // Create plane through the gizmo position
    ref_axis_drag_plane_world.current.setFromNormalAndCoplanarPoint(plane_normal_world, cursor_world_position);

    ref_axis_drag_start_gizmo_world_position.current.copy(cursor_world_position);
    ref_axis_drag_start_node_world_position.current.copy(node_world_position);

    highlight_axis_plane(plane_name);
  }

  function get_plane_normal_from_name(plane_name: EOpenPoseCursorPlane): THREE.Vector3 {
    switch (plane_name) {
      case EOpenPoseCursorPlane.XY:
        return new THREE.Vector3(0, 0, 1);
      case EOpenPoseCursorPlane.XZ:
        return new THREE.Vector3(0, 1, 0);
      case EOpenPoseCursorPlane.YZ:
        return new THREE.Vector3(1, 0, 0);
      default:
        return new THREE.Vector3(0, 0, 1);
    }
  }

  function axis_end_drag() {
    ref_axis_active_axis_name.current = EOpenPoseCursorAxis.NONE;
    ref_axis_active_plane_name.current = EOpenPoseCursorPlane.NONE;
    ref_axis_drag_axis_direction_world.current.set(0, 0, 0);
    ref_axis_drag_plane_world.current = new THREE.Plane();
    ref_axis_drag_start_gizmo_world_position.current.set(0, 0, 0);
    ref_axis_drag_start_node_world_position.current.set(0, 0, 0);

    highlight_axis(EOpenPoseCursorAxis.NONE);
    highlight_axis_plane(EOpenPoseCursorPlane.NONE);
  }

  function plane_begin_drag(plane_name: EOpenPoseCursorPlane, cursor_world_position: THREE.Vector3, node_world_position: THREE.Vector3) {
    ref_plane_active_plane_name.current = plane_name;

    const plane_normal_world = get_plane_normal_from_name(plane_name);

    // Create plane through the gizmo position with the correct normal
    ref_plane_drag_plane_world.current.setFromNormalAndCoplanarPoint(plane_normal_world, cursor_world_position);

    ref_plane_drag_start_gizmo_world_position.current.copy(cursor_world_position);
    ref_plane_drag_start_node_world_position.current.copy(node_world_position);

    const plane_index = ref_custom_cursor_plane.current.get_plane_index(plane_name);

    if (plane_index >= 0) ref_custom_cursor_plane.current.set_plane_to_solid(plane_index);
  }

  function plane_end_drag() {
    ref_plane_active_plane_name.current = EOpenPoseCursorPlane.NONE;
    ref_plane_drag_plane_world.current = new THREE.Plane();

    ref_plane_drag_start_gizmo_world_position.current.set(0, 0, 0);
    ref_plane_drag_start_node_world_position.current.set(0, 0, 0);

    highlight_plane(EOpenPoseCursorPlane.NONE);
  }

  function get_constrained_world_position(mouse_position_ndc: THREE.Vector2, camera_current: THREE.Camera): THREE.Vector3 {
    let out = new THREE.Vector3();

    const raycaster = new THREE.Raycaster();
    raycaster.setFromCamera(mouse_position_ndc, camera_current);

    const is_axis_active = ref_axis_active_axis_name.current !== EOpenPoseCursorAxis.NONE || ref_axis_active_plane_name.current !== EOpenPoseCursorPlane.NONE;

    if (is_axis_active) {
      // Handle axis cursor dragging
      if (ref_axis_active_plane_name.current !== EOpenPoseCursorPlane.NONE) {
        // Plane constraint for axis cursor
        const intersection_world = new THREE.Vector3();
        if (raycaster.ray.intersectPlane(ref_axis_drag_plane_world.current, intersection_world)) {
          out = intersection_world;
        } else {
          // Fallback if no intersection
          const camera_position = camera_current.position.clone();
          const distance = camera_position.distanceTo(ref_axis_drag_start_node_world_position.current);
          out = camera_position.clone().add(raycaster.ray.direction.clone().multiplyScalar(distance));
        }
      }

      if (ref_axis_active_axis_name.current !== EOpenPoseCursorAxis.NONE) {
        // Axis constraint - project ray onto axis
        const camera_position = camera_current.position.clone();
        const ray_direction = raycaster.ray.direction.clone();

        // Find the closest point on the axis to the mouse ray
        const axis_origin = ref_axis_drag_start_gizmo_world_position.current.clone();
        const axis_direction = ref_axis_drag_axis_direction_world.current.clone().normalize();

        // Vector from axis origin to camera
        const camera_to_axis = camera_position.clone().sub(axis_origin);

        // Project camera-to-axis onto the plane perpendicular to axis
        const perpendicular_component = camera_to_axis.clone().sub(axis_direction.clone().multiplyScalar(camera_to_axis.dot(axis_direction)));

        // Project ray direction onto the same perpendicular plane
        const ray_perpendicular = ray_direction
          .clone()
          .sub(axis_direction.clone().multiplyScalar(ray_direction.dot(axis_direction)))
          .normalize();

        // Find intersection parameter
        const denominator = ray_perpendicular.dot(ray_perpendicular);
        if (Math.abs(denominator) > 1e-6) {
          const t = -perpendicular_component.dot(ray_perpendicular) / denominator;
          const intersection_point = camera_position.clone().add(ray_direction.clone().multiplyScalar(t));

          // Project onto axis
          const axis_param = intersection_point.clone().sub(axis_origin).dot(axis_direction);
          out = axis_origin.clone().add(axis_direction.multiplyScalar(axis_param));
        } else {
          out = axis_origin;
        }
      }
    } else if (ref_plane_active_plane_name.current !== EOpenPoseCursorPlane.NONE) {
      // Handle plane cursor dragging
      const intersection_world = new THREE.Vector3();
      if (raycaster.ray.intersectPlane(ref_plane_drag_plane_world.current, intersection_world)) {
        out = intersection_world;
      } else {
        // Fallback if no intersection
        const camera_position = camera_current.position.clone();
        const distance = camera_position.distanceTo(ref_plane_drag_start_node_world_position.current);

        out = camera_position.clone().add(raycaster.ray.direction.clone().multiplyScalar(distance));
      }
    }

    return out;
  }

  function process_plane_cursor_mouse_down(raycaster: THREE.Raycaster): boolean {
    let out = false;

    const intersections = raycaster.intersectObject(ref_custom_cursor_plane.current, true);
    console.log('process_plane_cursor_mouse_down intersections', intersections);
    if (intersections.length === 0) return out;

    const intersected_object = intersections[0].object;
    console.log('process_plane_cursor_mouse_down intersected_object', intersected_object);

    if (intersected_object.is_mesh_with_name(CURSOR_PART_PLANE)) {
      ref_is_mouse_down.current = true;

      const world_position_cursor = ref_custom_cursor_plane.current.position.clone();
      console.log('process_plane_cursor_mouse_down world_position_cursor', world_position_cursor);

      const world_position_node = ref_node_selected.current.get_world_position_zero_offset();
      console.log('process_plane_cursor_mouse_down world_position_node', world_position_node);

      const plane_name = intersected_object.name as EOpenPoseCursorPlane;
      console.log('process_plane_cursor_mouse_down plane_name', plane_name);

      plane_begin_drag(plane_name, world_position_cursor, world_position_node);
      highlight_plane(plane_name);
      refresh_selected_node_info_from_current();

      out = true;
    }

    return out;
  }

  function process_axis_cursor_cylinder_or_cone_mouse_down(intersected_object: THREE.Object3D): boolean {
    const axis_name = intersected_object.name.charAt(0) as EOpenPoseCursorAxis;
    assert_type_enum(axis_name, EOpenPoseCursorAxis);

    ref_is_mouse_down.current = true;

    const world_position_cursor = ref_custom_cursor_axis.current.position.clone();

    const world_position_node = ref_node_selected.current.get_world_position_zero_offset();

    axis_begin_axis_drag(axis_name, world_position_cursor, world_position_node);

    highlight_axis(axis_name);

    refresh_selected_node_info_from_current();

    return true;
  }

  function process_axis_cursor_plane_mouse_down(intersected_object: THREE.Object3D): boolean {
    const plane_name = intersected_object.name as EOpenPoseCursorPlane;
    assert_type_enum(plane_name, EOpenPoseCursorPlane);

    ref_is_mouse_down.current = true;

    const world_position_cursor = ref_custom_cursor_axis.current.position.clone();

    const world_position_node = ref_node_selected.current.get_world_position_zero_offset();

    axis_begin_plane_drag(plane_name, world_position_cursor, world_position_node);

    highlight_axis_plane(plane_name);

    refresh_selected_node_info_from_current();

    return true;
  }

  function process_node_drag(mouse_position_ndc: THREE.Vector2, camera_current: THREE.Camera) {
    console.log(`[DRAG] Processing node drag in mode: ${ref_interaction_mode.current}`);

    const constrained_world_position = get_constrained_world_position(mouse_position_ndc, camera_current);

    const node_object = ref_node_selected.current;
    const node_parent = node_object.parent;

    console.log(`[DRAG] Node: ${node_object.name}, Constrained position:`, constrained_world_position);

    // Convert world position to local coordinates
    let constrained_local: THREE.Vector3;
    if (node_parent) {
      constrained_local = node_parent.worldToLocal(constrained_world_position.clone());
    } else {
      constrained_local = constrained_world_position.clone();
    }

    // Special handling for NECK: move entire body instead of just the neck node
    const node_name = node_object.name.replace(ConstantsOpenPose.JOINT_SPHERE_NAME, '');
    if (node_name === EOpenPoseBodyNode.NECK) {
      console.log('[DRAG_NECK] Moving entire body instead of just neck node');
      
      // Apply movement constraints to world position
      const bounds = get_movement_bounds(service_open_pose.billboard_controller.height);
      apply_position_constraints_to_vector(constrained_world_position, bounds);
      
      // Move the entire torso (root body object) to the constrained world position
      const body_root = service_open_pose.body_controller.body;
      const current_body_position = body_root.position.clone();
      const neck_world_offset = node_object.get_world_position_zero_offset().sub(current_body_position);
      
      // Calculate new body position: desired neck position minus neck's offset from body root
      const new_body_position = constrained_world_position.clone().sub(neck_world_offset);
      body_root.position.copy(new_body_position);
      
      console.log(`[DRAG_NECK] Moved body to position:`, new_body_position);
    } else {
      // Normal node handling: update node position
      node_object.position.copy(constrained_local);
    }

    node_object.updateMatrixWorld(true);
    service_open_pose.body_controller.body.updateMatrixWorld(true);

    // Update cursor position to follow the node
    const world_position_visual = new THREE.Vector3();
    node_object.getWorldPosition(world_position_visual);
    show_cursor_at_position(world_position_visual);

    // Handle bone length constraints and update connected nodes
    if (ref_interaction_mode.current === EOPIM.DRAG_NODE) {
      const node_info = ref_node_selected_info.current;

      // In float length mode, we don't update stored bone lengths - bones should follow actual distances
      console.log(`[FLOAT_LENGTH] Skipping bone length updates - bones will use actual distances`);

      // Force update matrices
      service_open_pose.body_controller.body.updateMatrixWorld(true);

      // CRITICAL: Update parent group position to ensure synchronization
      // This ensures bones point to the correct location
      const node_name = node_info.node_name;
      if (node_object.parent && node_object.parent.name === node_name) {
        const node_world_pos = new THREE.Vector3();
        node_object.getWorldPosition(node_world_pos);

        if (node_object.parent.parent) {
          const parent_local_pos = node_object.parent.parent.worldToLocal(node_world_pos.clone());
          node_object.parent.position.copy(parent_local_pos);
          console.log(`[FLOAT_LENGTH] Updated parent group ${node_object.parent.name} position:`, parent_local_pos);
        }
      }

      // Final matrix update before creating bones
      service_open_pose.body_controller.body.updateMatrixWorld(true);

      // Update visual bones - use actual distances in float length mode (no stored distances)
      service_open_pose.body_controller.create_or_update_bones(false, false);
    } else if (ref_interaction_mode.current === EOPIM.DRAG_NODE_FIXED_LENGTH) {
      const node_info = ref_node_selected_info.current;
      const original_length = node_info.original_bone_lengths[node_info.node_name];

      if (original_length && original_length > 0) {
        // First update the node position to maintain fixed bone length
        console.log(`[FIXED_LENGTH] Maintaining bone length for ${node_info.node_name}: target=${original_length}`);

        maintain_bone_length_during_drag_enhanced(node_info, service_open_pose.body_controller, original_length);

        // Update stored bone distance to match the target length after position correction
        const bone_name = get_bone_name_for_node(node_info.node_name);
        if (bone_name) {
          service_open_pose.body_controller.set_bone_stored_distance(bone_name, original_length);
          console.log(`[FIXED_LENGTH] Updated stored distance for bone ${bone_name} to ${original_length}`);
        }

        // CRITICAL: Update matrices starting from the root to ensure proper hierarchy update
        service_open_pose.body_controller.body.updateMatrixWorld(true);

        // Debug: Verify the node's actual world position after matrix update
        const debug_world_pos = new THREE.Vector3();
        node_object.getWorldPosition(debug_world_pos);
        console.log(
          `[FIXED_LENGTH] Node actual world position after matrix update: ${debug_world_pos.x.toFixed(2)}, ${debug_world_pos.y.toFixed(
            2,
          )}, ${debug_world_pos.z.toFixed(2)}`,
        );

        // Get the corrected world position and update cursor
        const corrected_world_position = new THREE.Vector3();
        node_object.getWorldPosition(corrected_world_position);
        show_cursor_at_position(corrected_world_position);

        // Verify the bone length was maintained
        const verified_length = service_open_pose.body_controller.get_bone_length_for_node(node_info.node_name);
        console.log(`[FIXED_LENGTH] Verified bone length: ${verified_length} (target was ${original_length})`);

        // The bone distance should now match the actual distance between nodes after position correction
        // No need to set stored distance as the bone creation will use the stored bone distance correctly
      }

      // CRITICAL: Force update all matrices before creating bones
      console.log(`[FIXED_LENGTH] Forcing matrix update for entire body`);
      service_open_pose.body_controller.body.updateMatrixWorld(true);

      // Update visual bones - use stored distances in fixed length mode
      service_open_pose.body_controller.create_or_update_bones(false, true);
    }

    // Update projection and visuals
    service_open_pose.projection_controller.update_body_projection();
    enforce_selected_node_visual();
  }

  function process_hover_plane(raycaster: THREE.Raycaster, previous_hovered_plane_name: EOpenPoseCursorPlane) {
    const intersections = raycaster.intersectObject(ref_custom_cursor_plane.current, true);
    let hovered_plane_name: EOpenPoseCursorPlane = EOpenPoseCursorPlane.NONE;

    if (intersections.length > 0) {
      const intersect_object = intersections[0].object;

      if (intersect_object.is_mesh_with_name(CURSOR_PART_PLANE)) hovered_plane_name = assert_type_enum_return(intersect_object.name, EOpenPoseCursorPlane);
    }

    if (hovered_plane_name !== previous_hovered_plane_name) {
      highlight_plane(hovered_plane_name);
      return hovered_plane_name;
    }

    return previous_hovered_plane_name;
  }

  function process_hover_axis(raycaster: THREE.Raycaster, previous_hovered_axis_name: EOpenPoseCursorAxis, previous_hovered_plane_name: EOpenPoseCursorPlane) {
    const intersections = raycaster.intersectObject(ref_custom_cursor_axis.current, true);
    let hovered_axis_name: EOpenPoseCursorAxis = EOpenPoseCursorAxis.NONE;
    let hovered_plane_name: EOpenPoseCursorPlane = EOpenPoseCursorPlane.NONE;

    if (intersections.length > 0) {
      const intersect_object = intersections[0].object;

      if (intersect_object.name.includes(CURSOR_PART_CYLINDER) || intersect_object.name.includes(CURSOR_PART_CONE)) {
        hovered_axis_name = intersect_object.name.charAt(0) as EOpenPoseCursorAxis;
        ref_custom_cursor_axis.current.update_children_visibility(CURSOR_PART_PLANE, false);
      } else if (intersect_object.name.includes(CURSOR_PART_PLANE)) {
        hovered_plane_name = intersect_object.name as EOpenPoseCursorPlane;
        ref_custom_cursor_axis.current.update_children_visibility(CURSOR_PART_PLANE, true);
      }
    } else {
      ref_custom_cursor_axis.current.update_children_visibility(CURSOR_PART_PLANE, true);
    }

    if (hovered_axis_name !== previous_hovered_axis_name) {
      highlight_axis(hovered_axis_name);
      previous_hovered_axis_name = hovered_axis_name;
    }

    if (hovered_plane_name !== previous_hovered_plane_name) {
      highlight_axis_plane(hovered_plane_name);
      previous_hovered_plane_name = hovered_plane_name;
    }

    return { previous_hovered_axis_name, previous_hovered_plane_name };
  }

  function process_drag_node_end() {
    axis_end_drag();
    plane_end_drag();

    refresh_selected_node_info_from_current();

    highlight_plane(EOpenPoseCursorPlane.NONE);
    highlight_axis(EOpenPoseCursorAxis.NONE);
    highlight_axis_plane(EOpenPoseCursorPlane.NONE);
  }

  function on_transform_change() {
    console.log('🔵🔵🔵 TRANSFORM CHANGE EVENT! 🔵🔵🔵');
    ensure_selection_ready();

    const selected_object = service_open_pose.transform_controls.object;
    console.log('[TRANSFORM_CHANGE] Selected object:', selected_object?.name);
    console.log('[TRANSFORM_CHANGE] Transform mode:', service_open_pose.transform_controls.mode);

    if (selected_object?.name === EOpenPoseBodyNode.NECK || selected_object?.name === EOpenPoseBodyGroup.TORSO) {
      const bounds = get_movement_bounds(service_open_pose.billboard_controller.height);
      apply_position_constraints(selected_object, bounds);
    }

    service_open_pose.body_controller.create_or_update_bones(false, false);

    service_open_pose.projection_controller.update_body_projection();

    enforce_selected_node_visual();
  }

  function on_transform_mouse_down() {
    console.log('🔴🔴🔴 TRANSFORM MOUSE DOWN! 🔴🔴🔴');
  }

  function on_transform_mouse_up() {
    console.log('🟢🟢🟢 TRANSFORM MOUSE UP! 🟢🟢🟢');
  }

  function on_rotate_mode(node_name_string: string, object_3d: Object3D) {
    console.log('🟢🟢🟢 ON_ROTATE_MODE CALLED! NODE:', node_name_string, '🟢🟢🟢');
    console.log('[ON_ROTATE_MODE] Starting rotate mode for:', node_name_string);
    
    const node_name = assert_type_enum_return(node_name_string, EOpenPoseBodyNode);
    const pickable = is_pickable(node_name);
    console.log('[ON_ROTATE_MODE] Node pickable:', pickable);

    if (!pickable) {
      object_3d = find_ancestor_pickable(object_3d);
      console.log('[ON_ROTATE_MODE] Found ancestor pickable:', object_3d.name);
    }

    console.log('[ON_ROTATE_MODE] Attaching transform controls to:', object_3d.name);
    service_open_pose.transform_controls.setMode(EOpenPoseTransformControlsMode.ROTATE);
    service_open_pose.transform_controls.setSpace(EOpenPoseTransformControlsSpace.WORLD);
    service_open_pose.transform_controls.attach(object_3d);

    ref_node_selected.current = object_3d;
    select_node_and_show_cursor(object_3d);

    trigger_select_event();

    hide_cursors();
    
    console.log('[ON_ROTATE_MODE] Rotate mode setup completed');
  }

  function on_drag_node_fixed_length_mode(node_name_string: string, object_3d: Object3D) {
    const node_name = assert_type_enum_return(node_name_string, EOpenPoseBodyNode);

    const is_valid_node = is_pickable(node_name) || is_hand_or_foot(node_name);

    if (!is_valid_node) object_3d = find_ancestor_pickable_hand_or_foot(object_3d);

    //todo: extend object3d
    if (is_hand_or_foot(node_name)) {
      const connected_node = get_connected_node_for_hand_foot(object_3d.name as OpenPoseLimbs);

      const actual_node = service_open_pose.body_controller.get_node(connected_node)?.object;

      if (actual_node) object_3d = actual_node;
    }

    select_node_and_show_cursor(object_3d);

    const original_bone_lengths: Record<string, number> = {};

    const bone_length = service_open_pose.body_controller.get_bone_length_for_node(object_3d.name);
    console.log(`[FIXED_LENGTH_SETUP] Got bone length for ${object_3d.name}: ${bone_length}`);

    if (bone_length > 0) {
      original_bone_lengths[node_name] = bone_length;
      console.log(`[FIXED_LENGTH_SETUP] Stored original length for ${node_name}: ${bone_length}`);
    }

    ref_node_selected_info.current = {
      node: object_3d,
      node_name,
      body: service_open_pose.body_controller.body,
      original_position: object_3d.position.clone(),
      parent_bone: object_3d.parent,
      child_bones: get_child_bones(object_3d),
      parent_nodes: get_parent_nodes(object_3d),
      child_nodes: get_child_nodes(object_3d),
      original_bone_lengths,
    };

    const world_position = object_3d.get_world_position_zero_offset();

    show_cursor_at_position(world_position);

    trigger_select_event();
  }

  function on_drag_node_float_length_mode(node_name_string: string, object_3d: Object3D) {
    const node_name = assert_type_enum_return(node_name_string, EOpenPoseBodyNode);

    const is_valid_node = is_pickable(node_name) || is_hand_or_foot(node_name);

    if (!is_valid_node) object_3d = find_ancestor_pickable_hand_or_foot(object_3d);

    if (is_hand_or_foot(node_name)) {
      const connected_node = get_connected_node_for_hand_foot(object_3d.name as OpenPoseLimbs);

      const actual_node = service_open_pose.body_controller.get_node(connected_node)?.object;
      if (actual_node) object_3d = actual_node;
    }

    select_node_and_show_cursor(object_3d);

    const original_bone_lengths: Record<string, number> = {};

    ref_node_selected_info.current = {
      node: object_3d,
      node_name,
      body: service_open_pose.body_controller.body,
      original_position: object_3d.position.clone(),
      parent_bone: object_3d.parent,
      child_bones: get_child_bones(object_3d),
      parent_nodes: get_parent_nodes(object_3d),
      child_nodes: get_child_nodes(object_3d),
      original_bone_lengths,
    };

    const world_position = object_3d.get_world_position_zero_offset();

    show_cursor_at_position(world_position);

    trigger_select_event();
  }

  function show_cursor_at_position(position: THREE.Vector3) {
    if (ref_interaction_mode.current === EOPIM.DRAG_NODE_FIXED_LENGTH) {
      service_open_pose.scene.add(ref_custom_cursor_plane.current);

      ref_custom_cursor_plane.current.position.copy(position);
      ref_custom_cursor_plane.current.visible = true;

      if (ref_custom_cursor_axis.current) ref_custom_cursor_axis.current.visible = false;
    } else if (ref_interaction_mode.current === EOPIM.DRAG_NODE) {
      service_open_pose.scene.add(ref_custom_cursor_axis.current);

      ref_custom_cursor_axis.current.position.copy(position);
      ref_custom_cursor_axis.current.visible = true;

      if (ref_custom_cursor_plane.current) ref_custom_cursor_plane.current.visible = false;
    }
  }

  function hide_cursors() {
    ref_custom_cursor_plane.current.visible = false;
    ref_custom_cursor_axis.current.visible = false;
  }

  function highlight_plane(plane_name: string) {
    for (const child of ref_custom_cursor_plane.current.children) {
      if (!(child instanceof THREE.Mesh) || !child.name.includes(CURSOR_PART_PLANE)) continue;

      const material = child.material as THREE.MeshBasicMaterial;
      const plane_index = ref_custom_cursor_plane.current.get_plane_index(child.name);

      if (child.name === plane_name) {
        material.opacity = CURSOR_PLANE_HOVER_OPACITY;
        ref_custom_cursor_plane.current.set_plane_to_solid(plane_index);
      } else {
        material.opacity = plane_name ? CURSOR_PLANE_UNHOVER_OPACITY : CURSOR_PLANE_DEFAULT_OPACITY;
        ref_custom_cursor_plane.current.set_plane_to_frame(plane_index);
      }

      material.needsUpdate = true;
    }
  }

  function highlight_axis(axis_name: EOpenPoseCursorAxis) {
    ref_custom_cursor_axis.current.highlight_axis(axis_name);
  }

  function highlight_axis_plane(plane_name: EOpenPoseCursorPlane) {
    ref_custom_cursor_axis.current.highlight_plane(plane_name);
  }

  function set_node_selection_visual(node_object: Object3D, is_selected: boolean) {
    const sphere = get_node_sphere_mesh(node_object);

    sphere.userData.original_scalar = sphere.scale.x;
    const original_scalar = sphere.userData.original_scalar;

    const target_scalar = is_selected ? original_scalar / SELECTED_NODE_SCALE_DIVISOR : original_scalar;
    sphere.scale.setScalar(target_scalar);

    sphere.updateMatrixWorld(true);
  }

  function select_node_and_show_cursor(node_object: Object3D) {
    service_open_pose.body_controller.body.updateMatrixWorld(true);

    const previous_selected_node = ref_node_selected.current;
    if (previous_selected_node && previous_selected_node !== node_object) set_node_selection_visual(previous_selected_node, false);

    set_node_selection_visual(node_object, true);

    ref_node_selected.current = node_object;

    const world_position = node_object.get_world_position_zero_offset();

    show_cursor_at_position(world_position);

    trigger_select_event();
  }

  function enforce_selected_node_visual() {
    const node = ref_node_selected.current;

    set_node_selection_visual(node, true);
  }

  function ensure_node_selected() {
    const neck = service_open_pose.body_controller.get_node(EOpenPoseBodyNode.NECK).object;

    select_node_and_show_cursor(neck);
  }

  function refresh_selected_node_info_from_current() {
    const node_object = ref_node_selected.current;

    const node_name = node_object.name.includes(ConstantsOpenPose.JOINT_SPHERE_NAME)
      ? node_object.name.replace(ConstantsOpenPose.JOINT_SPHERE_NAME, '')
      : node_object.name;

    const original_bone_lengths: Record<string, number> = {};

    if (ref_interaction_mode.current === EOPIM.DRAG_NODE_FIXED_LENGTH) {
      const bone_length = service_open_pose.body_controller.get_bone_length_for_node(node_name);
      console.log(`[REFRESH_INFO] Getting bone length for ${node_name}: ${bone_length}`);

      if (bone_length > 0) {
        original_bone_lengths[node_name] = bone_length;
        console.log(`[REFRESH_INFO] Stored bone length for ${node_name}: ${bone_length}`);
      }
    }

    ref_node_selected_info.current = {
      node: node_object,
      node_name,
      body: service_open_pose.body_controller.body,
      original_position: node_object.position.clone(),
      parent_bone: node_object.parent,
      child_bones: get_child_bones(node_object),
      parent_nodes: get_parent_nodes(node_object),
      child_nodes: get_child_nodes(node_object),
      original_bone_lengths,
    };
  }

  function trigger_select_event() {
    ref_select_event_manager.current.TriggerEvent(service_open_pose.body_controller);
    update_bones();
  }

  function set_interaction_mode(mode: EOPIM) {
    console.log('🟡🟡🟡 SET_INTERACTION_MODE CALLED! MODE:', mode, '🟡🟡🟡');
    console.log('[SET_INTERACTION_MODE] Setting mode to:', mode);
    
    reset_transform_control();
    ref_interaction_mode.current = mode;

    const selected_node = ref_node_selected.current;
    console.log('[SET_INTERACTION_MODE] Selected node:', selected_node?.name || 'none');
    console.log('[SET_INTERACTION_MODE] JOINT_SPHERE_NAME:', ConstantsOpenPose.JOINT_SPHERE_NAME);
    console.log('[SET_INTERACTION_MODE] Node name includes JOINT_SPHERE_NAME:', selected_node?.name?.includes(ConstantsOpenPose.JOINT_SPHERE_NAME));

    if (!selected_node || !selected_node.name.includes(ConstantsOpenPose.JOINT_SPHERE_NAME)) {
      console.log('[SET_INTERACTION_MODE] Early return - invalid selected node');
      return;
    }

    const node_name = selected_node.name.replace(ConstantsOpenPose.JOINT_SPHERE_NAME, '');
    console.log('[SET_INTERACTION_MODE] Extracted node name:', node_name);

    const selected_node_name = assert_type_enum_return(node_name, EOpenPoseBodyNode);
    console.log('[SET_INTERACTION_MODE] Selected node name as enum:', selected_node_name);

    if (mode === EOPIM.ROTATE) {
      console.log('[SET_INTERACTION_MODE] Calling on_rotate_mode');
      on_rotate_mode(selected_node_name, selected_node);
    } else if (mode === EOPIM.DRAG_NODE) {
      console.log('[SET_INTERACTION_MODE] Calling on_drag_node_float_length_mode');
      on_drag_node_float_length_mode(selected_node_name, selected_node);
    } else if (mode === EOPIM.DRAG_NODE_FIXED_LENGTH) {
      console.log('[SET_INTERACTION_MODE] Calling on_drag_node_fixed_length_mode');
      on_drag_node_fixed_length_mode(selected_node_name, selected_node);
    } else not_implemented_type(mode);
  }

  function reset_transform_control() {
    const transform_control_old = service_open_pose.transform_controls;
    const camera_current = service_open_pose.camera_controller.get_camera_current();

    transform_control_old.detach();

    transform_control_old.setMode(EOpenPoseTransformControlsMode.ROTATE);
    transform_control_old.setSpace(EOpenPoseTransformControlsSpace.LOCAL);

    transform_control_old.camera = camera_current;
    ref_node_selected_info.current = EThreejsStub.SELECTED_NODE_INFO;
  }

  function get_selected_part(): Object3D {
    ensure_selection_ready();

    return (service_open_pose.transform_controls.object as Object3D) ?? ref_node_selected.current ?? EThreejsStub.OBJECT;
  }

  function detach_transform_control() {
    service_open_pose.transform_controls.detach();
    update_bones();
  }

  function clear_bodies_from_scene() {
    if (!service_open_pose.body_controller.body) debugger;

    service_open_pose.scene.remove(service_open_pose.body_controller.body);
    service_open_pose.body_controller.body.removeFromParent();

    hide_cursors();

    ref_node_selected.current = EThreejsStub.OBJECT;
  }

  function apply_body_size_dinamic(size_percent: number) {
    // Check if body controller is ready
    if (!service_open_pose.body_controller || !service_open_pose.body_controller.body) {
      console.log('[APPLY_BODY_SIZE] Body controller not ready, skipping size change');
      return;
    }

    // Prevent recursive changes by checking if we're already at this size
    if (Math.abs(ref_previous_body_size.current - size_percent) < 0.1) {
      return;
    }

    console.log(`[APPLY_BODY_SIZE] Changing bone lengths to ${size_percent}%`);

    // Calculate scale factor from default size
    const scale_factor = size_percent / DEFAULT_BODY_SIZE;

    ref_previous_body_size.current = size_percent;

    // Get all bone base lengths if not stored yet
    if (Object.keys(ref_base_bone_lengths.current).length === 0) {
      store_base_bone_lengths();
    }

    // Debug: check specific leg bones
    const critical_leg_bones = ['hip_left_link_knee_left', 'hip_right_link_knee_right', 'knee_left_link_ankle_left', 'knee_right_link_ankle_right'];

    console.log('[DEBUG_LEG_BONES] Checking critical leg bones:');
    critical_leg_bones.forEach(bone_name => {
      const has_base_length = !!ref_base_bone_lengths.current[bone_name];
      const has_bone_object = !!service_open_pose.body_controller.bones[bone_name];
      console.log(`  ${bone_name}: base_length=${has_base_length}, bone_object=${has_bone_object}`);

      // If bone object exists but no base length, add it now
      if (has_bone_object && !has_base_length) {
        const bone = service_open_pose.body_controller.bones[bone_name];
        const length = bone.get_distance();
        ref_base_bone_lengths.current[bone_name] = length;
        console.log(`  ✓ Added missing base length for ${bone_name}: ${length.toFixed(1)}`);
      }
    });

    // Scale each bone length directly without changing node positions or thickness
    for (const bone_name in service_open_pose.body_controller.bones) {
      const bone = service_open_pose.body_controller.bones[bone_name];
      if (bone && ref_base_bone_lengths.current[bone_name]) {
        const base_length = ref_base_bone_lengths.current[bone_name];
        const new_length = base_length * scale_factor;
        bone.set_distance(new_length);
        console.log(`[BONE_SCALE] ${bone_name}: ${base_length.toFixed(1)} -> ${new_length.toFixed(1)} (scale: ${scale_factor.toFixed(2)})`);
      } else {
        console.log(`[BONE_SCALE] Skipping ${bone_name}: bone=${!!bone}, base_length=${!!ref_base_bone_lengths.current[bone_name]}`);
      }
    }

    // Recreate bones with new lengths - this will update bone geometry
    service_open_pose.body_controller.create_or_update_bones(false, false);

    service_open_pose.projection_controller.update_body_projection();
    enforce_selected_node_visual();
  }

  // Store base bone lengths when body is first created
  const ref_base_bone_lengths = useRef<Record<string, number>>({});

  function store_base_bone_lengths() {
    const body_controller = service_open_pose.body_controller;
    if (!body_controller) {
      console.log('[STORE_BASE_LENGTHS] No body controller available');
      return;
    }

    // First, log all available bone names
    console.log('[STORE_BASE_LENGTHS] All available bones:');
    const all_bone_names = Object.keys(body_controller.bones);
    all_bone_names.forEach(name => {
      const bone = body_controller.bones[name];
      const length = bone ? bone.get_distance() : 'null';
      console.log(`  - ${name}: ${length}`);
    });

    let stored_count = 0;
    for (const bone_name in body_controller.bones) {
      const bone = body_controller.bones[bone_name];
      if (bone) {
        const length = bone.get_distance();
        ref_base_bone_lengths.current[bone_name] = length;
        console.log(`[STORE_BASE_LENGTHS] ${bone_name}: ${length.toFixed(1)}`);
        stored_count++;
      }
    }

    console.log(`[STORE_BASE_LENGTHS] Stored ${stored_count} base bone lengths`);
  }

  function ensure_selection_ready(): void {
    let selected_node = ref_node_selected.current;

    if (selected_node === EThreejsStub.OBJECT) {
      const neck_object = service_open_pose.body_controller.get_node(EOpenPoseBodyNode.NECK)?.object ?? EThreejsStub.OBJECT;
      ref_node_selected.current = neck_object;

      if (neck_object !== EThreejsStub.OBJECT) {
        select_node_and_show_cursor(neck_object);
        enforce_selected_node_visual();
      }

      selected_node = neck_object;
    }

    if (ref_interaction_mode.current === EOPIM.ROTATE && !service_open_pose.transform_controls.object && selected_node !== EThreejsStub.OBJECT)
      service_open_pose.transform_controls.attach(selected_node);
  }

  function create_bounding_box_helper() {
    service_open_pose.scene.remove(ref_bounds_helper.current);

    ref_bounds_helper.current.geometry.dispose();

    if (ref_bounds_helper.current.material instanceof THREE.Material) ref_bounds_helper.current.material.dispose();

    ref_bounds_helper.current = EThreejsStub.LINE_SEGMENTS;

    const bounds = get_movement_bounds(service_open_pose.billboard_controller.height);
    const width = bounds.x.max - bounds.x.min;
    const height = bounds.y.max - bounds.y.min;
    const depth = bounds.z.max - bounds.z.min;

    const geometry = new THREE.BoxGeometry(width, height, depth);
    const edges = new THREE.EdgesGeometry(geometry);
    const material = new THREE.LineBasicMaterial({ color: BOUNDING_BOX_COLOR, transparent: true });
    const helper = new THREE.LineSegments(edges, material);

    helper.position.set((bounds.x.min + bounds.x.max) / 2, (bounds.y.min + bounds.y.max) / 2, (bounds.z.min + bounds.z.max) / 2);
    helper.name = BOUNDING_BOX_NAME;
    ref_bounds_helper.current = helper;

    service_open_pose.scene.add(helper);
  }

  function create_camera_bounds_helper() {
    service_open_pose.scene.remove(ref_camera_bounds_helper.current);

    ref_camera_bounds_helper.current.geometry.dispose();
    if (ref_camera_bounds_helper.current.material instanceof THREE.Material) ref_camera_bounds_helper.current.material.dispose();

    ref_camera_bounds_helper.current = EThreejsStub.LINE_SEGMENTS;

    const camera_bounds = service_open_pose.camera_controller.bounds;
    const width = camera_bounds.x.max - camera_bounds.x.min;
    const height = camera_bounds.y.max - camera_bounds.y.min;
    const depth = camera_bounds.z.max - camera_bounds.z.min;

    const geometry = new THREE.BoxGeometry(width, height, depth);
    const edges = new THREE.EdgesGeometry(geometry);
    const material = new THREE.LineBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.8 });
    const helper = new THREE.LineSegments(edges, material);

    helper.position.set(
      (camera_bounds.x.min + camera_bounds.x.max) / 2,
      (camera_bounds.y.min + camera_bounds.y.max) / 2,
      (camera_bounds.z.min + camera_bounds.z.max) / 2,
    );
    helper.name = 'camera_bounds_helper';
    ref_camera_bounds_helper.current = helper;

    service_open_pose.scene.add(helper);
    console.log('[CAMERA_BOUNDS] Created camera bounds helper with dimensions:', { width, height, depth });
  }

  function create_body_bounds_helper() {
    service_open_pose.scene.remove(ref_body_bounds_helper.current);

    ref_body_bounds_helper.current.geometry.dispose();
    if (ref_body_bounds_helper.current.material instanceof THREE.Material) ref_body_bounds_helper.current.material.dispose();

    ref_body_bounds_helper.current = EThreejsStub.LINE_SEGMENTS;

    const body_bounds = get_movement_bounds(service_open_pose.billboard_controller.height);

    // Create a 2D rectangle frame (only X and Y axes, Z is constrained to 0 for NECK)
    const points = [];
    const z = 0; // Body movement is constrained to Z = 0

    // Create rectangle outline
    points.push(new THREE.Vector3(body_bounds.x.min, body_bounds.y.min, z)); // Bottom-left
    points.push(new THREE.Vector3(body_bounds.x.max, body_bounds.y.min, z)); // Bottom-right
    points.push(new THREE.Vector3(body_bounds.x.max, body_bounds.y.max, z)); // Top-right
    points.push(new THREE.Vector3(body_bounds.x.min, body_bounds.y.max, z)); // Top-left
    points.push(new THREE.Vector3(body_bounds.x.min, body_bounds.y.min, z)); // Back to bottom-left

    const geometry = new THREE.BufferGeometry().setFromPoints(points);
    const material = new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.8 });
    const helper = new THREE.Line(geometry, material);

    helper.name = 'body_bounds_helper';
    ref_body_bounds_helper.current = helper as any; // Cast to LineSegments for compatibility

    service_open_pose.scene.add(helper);
    console.log('[BODY_BOUNDS] Created body bounds helper as 2D frame at Z=0');
  }

  function show_bounds_helpers() {
    create_camera_bounds_helper();
    create_body_bounds_helper();
  }

  function recreate_body_from_template() {
    console.log('[RECREATE_BODY] Creating fresh body from template');
    
    // Remove old body from scene
    const old_body = service_open_pose.body_controller.body;
    if (old_body) {
      service_open_pose.scene.remove(old_body);
      console.log('[RECREATE_BODY] Removed old body from scene');
    }
    
    // Create fresh body from template - exactly like service_open_pose.init() does
    const new_body = open_pose_body_create_template();
    service_open_pose.body_controller = new OpenPoseBodyController(new_body);
    service_open_pose.body_controller.create_or_update_bones(true, false);
    service_open_pose.scene.add(new_body);
    
    // Hide any existing cursors since we have a fresh body
    hide_cursors();
    
    // Reset transform controls to work with new body objects
    service_open_pose.transform_controls.detach();
    
    console.log('[RECREATE_BODY] Created and added fresh body to scene');
  }

  function hide_bounds_helpers() {
    service_open_pose.scene.remove(ref_camera_bounds_helper.current);
    service_open_pose.scene.remove(ref_body_bounds_helper.current);
  }

  function maintain_bone_length_during_drag_enhanced(node_info: OpenPoseSelectedNodeInfo, open_pose_body: OpenPoseBodyController, target_length: number) {
    const { node, node_name } = node_info;
    if (!open_pose_body.has_node(node_name)) {
      console.log(`[MAINTAIN_BONE] Node ${node_name} not found in body controller`);
      return;
    }

    let parent_node: THREE.Object3D | null = null;

    // Find parent node using bone parent map first
    if (BONE_PARENT_MAP[node_name]) {
      const parent_name = BONE_PARENT_MAP[node_name];
      console.log(`[MAINTAIN_BONE] Using parent from map: ${node_name} -> ${parent_name}`);
      const parent_obj = open_pose_body.get_node(parent_name) || open_pose_body.get_group(parent_name);
      parent_node = parent_obj ? (parent_obj instanceof OpenPoseBodyNode ? parent_obj.object : parent_obj) : null;
    }

    // Fallback to using the Three.js parent
    if (!parent_node) {
      parent_node = node.parent;
      console.log(`[MAINTAIN_BONE] Using Three.js parent:`, parent_node?.name || 'null');
    }

    if (!parent_node) {
      console.log(`[MAINTAIN_BONE] No parent found for ${node_name}`);
      return;
    }

    // Update world matrices first to get accurate positions
    parent_node.updateMatrixWorld(true);
    node.updateMatrixWorld(true);

    // Get world positions
    const current_world_position = new THREE.Vector3();
    const parent_world_position = new THREE.Vector3();

    node.getWorldPosition(current_world_position);
    parent_node.getWorldPosition(parent_world_position);

    // Calculate direction from parent to current node
    const direction = current_world_position.clone().sub(parent_world_position);
    const current_length = direction.length();

    console.log(`[MAINTAIN_BONE] Current length: ${current_length}, target: ${target_length}`);
    console.log(`[MAINTAIN_BONE] Parent position:`, parent_world_position);
    console.log(`[MAINTAIN_BONE] Node position before:`, current_world_position);

    if (current_length > 0.001) {
      // Normalize direction and apply target length
      direction.normalize();
      const world_position_new = parent_world_position.clone().add(direction.multiplyScalar(target_length));

      console.log(`[MAINTAIN_BONE] New world position:`, world_position_new);

      // Convert world position back to local coordinates for the node's parent
      if (node.parent) {
        console.log(`[MAINTAIN_BONE] Node parent:`, node.parent.name);
        const position_local = node.parent.worldToLocal(world_position_new.clone());
        console.log(`[MAINTAIN_BONE] New local position:`, position_local);
        console.log(`[MAINTAIN_BONE] Node.position before update:`, node.position);
        node.position.copy(position_local);
        console.log(`[MAINTAIN_BONE] Node.position after update:`, node.position);

        // Force update node's matrix immediately
        node.updateMatrix();
        console.log(`[MAINTAIN_BONE] Node matrix updated`);

        // CRITICAL: Also update the parent group position to match the node
        // This is needed because bones are created between groups, not nodes
        if (node.parent && node.parent.name === node_name) {
          // The parent group should have the same world position as the node
          const node_world_pos = new THREE.Vector3();
          node.getWorldPosition(node_world_pos);

          if (node.parent.parent) {
            // Convert node's world position to parent group's local space
            const parent_local_pos = node.parent.parent.worldToLocal(node_world_pos.clone());
            node.parent.position.copy(parent_local_pos);
            console.log(`[MAINTAIN_BONE] Updated parent group ${node.parent.name} position:`, parent_local_pos);
          }
        }
      } else {
        // If no parent, use world position directly
        console.log(`[MAINTAIN_BONE] No parent, using world position directly`);
        node.position.copy(world_position_new);
        node.updateMatrix();
      }
    } else {
      // Fallback: use default direction (towards positive X)
      console.log(`[MAINTAIN_BONE] Using fallback direction`);
      const default_direction = new THREE.Vector3(1, 0, 0);
      const world_position_new = parent_world_position.clone().add(default_direction.multiplyScalar(target_length));

      if (node.parent) {
        const position_local = node.parent.worldToLocal(world_position_new.clone());
        node.position.copy(position_local);
      } else {
        node.position.copy(world_position_new);
      }
    }

    // Force update matrices after position change
    node.updateMatrixWorld(true);

    // Verify the result
    const final_world_position = new THREE.Vector3();
    node.getWorldPosition(final_world_position);
    const final_length = final_world_position.distanceTo(parent_world_position);
    console.log(`[MAINTAIN_BONE] Final length: ${final_length}`);
    console.log(`[MAINTAIN_BONE] Node position after:`, final_world_position);
  }

  function update_bones() {
    const selected_node = ref_node_selected.current;

    const selected_node_name = selected_node.name ? selected_node.name.replace(JOINT_SPHERE_NAME, '') : '';
    console.log('[UPDATE_BONES] Selected node name:', selected_node_name);
    
    const connected_bone_names = selected_node ? get_connected_bone_names_for_node(selected_node_name) : [];
    console.log('[UPDATE_BONES] Connected bone names:', connected_bone_names);

    if (selected_node_name === EOpenPoseBodyNode.NECK) {
      console.log('[UPDATE_BONES] NECK selected - setting full opacity');
      set_full_opacity_for_body_links_and_joints(service_open_pose.body_controller.body, 1.0);
    } else {
      service_open_pose.body_controller.body.traverse(child => {
        if (child instanceof THREE.Mesh && child.name.includes(LINK_NAME)) {
          const material = child.material as MeshBasicMaterial;
          const is_connected = connected_bone_names.includes(child.name);
          material.opacity = is_connected ? 1.0 : 0.3;
          material.transparent = true;
          material.needsUpdate = true;
          
          if (is_connected) {
            console.log('[UPDATE_BONES] Bone highlighted:', child.name);
          }
        }
        if (child instanceof THREE.Mesh && child.name.includes(JOINT_SPHERE_NAME)) {
          const material = child.material as MeshBasicMaterial;

          const sphere_node_name = child.name.replace(JOINT_SPHERE_NAME, '');

          const is_connected = selected_node && (
            sphere_node_name === selected_node_name || 
            connected_bone_names.some(bone_name => bone_name.includes(sphere_node_name))
          );

          material.opacity = is_connected ? 1.0 : 0.3;
          material.transparent = true;
          material.needsUpdate = true;
          
          if (is_connected) {
            console.log('[UPDATE_BONES] Node highlighted:', sphere_node_name);
          }
        }

        if (child instanceof Bone && is_bone(child.name)) set_color(child);
      });
    }

    const selected_bone = get_selected_bone();

    if (selected_bone) {
      let bone = selected_bone;
      set_color(bone, COLOR_SELECTED_FOR_BONE);

      bone.traverseAncestors(ancestor => {
        if (!is_bone(ancestor.name)) return;

        set_color(ancestor as Bone);
      });

      while (true) {
        const child = bone.children.filter(object => object instanceof Bone);
        if (child.length !== 1) break;

        bone = child[0] as Bone;

        set_color(bone);
      }
    }
  }

  function get_selected_bone() {
    const part = get_selected_part();
    const is_select_bone = part && is_bone(part.name);

    return is_select_bone ? (part as Bone) : null;
  }

  function reset_body() {
    console.log('[RESET_SCENE] Starting scene reset...');

    try {
      clear_bodies_from_scene();
      console.log('[RESET_SCENE] Bodies cleared from scene');

      detach_transform_control();
      console.log('[RESET_SCENE] Transform control detached');

      const open_pose_body = service_open_pose.body_controller;
      open_pose_body.create_or_update_bones(true, false);
      console.log('[RESET_SCENE] Bones created/updated');

      // Ensure the body is added to the scene
      if (open_pose_body.body && !service_open_pose.scene.children.includes(open_pose_body.body)) {
        service_open_pose.scene.add(open_pose_body.body);
        console.log('[RESET_SCENE] Body added to scene');
      }

      open_pose_body.set_current_scale(1);
      console.log('[RESET_SCENE] Scale set to 1');

      // Store base bone lengths after creating the body
      store_base_bone_lengths();
      
      // Recreate body from template (fresh default pose)
      recreate_body_from_template();

      // After recreating body, get the new neck node sphere and select it
      const neck_node = service_open_pose.body_controller.get_node(EOpenPoseBodyNode.NECK);
      const neck_sphere_name = EOpenPoseBodyNode.NECK + ConstantsOpenPose.JOINT_SPHERE_NAME;
      const neck_sphere = neck_node.object.getObjectByName(neck_sphere_name);
      console.log('[RESET_BODY] Looking for neck sphere:', neck_sphere_name);
      console.log('[RESET_BODY] Found neck sphere:', neck_sphere?.name);
      
      if (neck_sphere) {
        ref_node_selected.current = neck_sphere;
        console.log('[RESET_BODY] Selected neck sphere:', neck_sphere.name);
      } else {
        console.warn('[RESET_BODY] Neck sphere not found, using bone node:', neck_node.object.name);
        ref_node_selected.current = neck_node.object;
      }

      const selected_object = ref_node_selected.current;
      const node_name_for_mode = selected_object.name.includes(ConstantsOpenPose.JOINT_SPHERE_NAME) 
        ? selected_object.name.replace(ConstantsOpenPose.JOINT_SPHERE_NAME, '') 
        : EOpenPoseBodyNode.NECK;
      
      if (ref_interaction_mode.current === EOPIM.ROTATE) {
        on_rotate_mode(node_name_for_mode as EOpenPoseBodyNode, selected_object);
      } else if (ref_interaction_mode.current === EOPIM.DRAG_NODE) {
        on_drag_node_float_length_mode(node_name_for_mode as EOpenPoseBodyNode, selected_object);
      } else if (ref_interaction_mode.current === EOPIM.DRAG_NODE_FIXED_LENGTH) {
        on_drag_node_fixed_length_mode(node_name_for_mode as EOpenPoseBodyNode, selected_object);
      }

      select_node_and_show_cursor(selected_object);
      enforce_selected_node_visual();
      
      // Force update bones highlighting
      update_bones();
      console.log('[RESET_BODY] Bones highlighting updated');

      ref_previous_body_size.current = DEFAULT_BODY_SIZE;
      service_open_pose.projection_controller.update_body_projection();
      create_bounding_box_helper();

      // Always show bounds helpers (camera bounds in black, body bounds in white)
      show_bounds_helpers();
      console.log('[RESET_SCENE] Bounds helpers shown');

      console.log('[RESET_SCENE] Scene reset completed successfully');
    } catch (error) {
      console.error('[RESET_SCENE] Error during scene reset:', error);
      throw error;
    }
  }

  return {
    on_mouse_down,
    on_mouse_move,
    on_mouse_up,
    reset_body,
    set_interaction_mode,
    apply_body_size_dinamic,
    show_bounds_helpers,
    hide_bounds_helpers,
  };
}
